<template>
  <div class="scorm-upload-manager">
    <!-- Upload Progress Modal -->
    <v-dialog
      v-model="isUploadModalOpen"
      max-width="500px"
      persistent
    >
      <v-card class="upload-modal-card">
        <v-card-title class="upload-modal-header d-flex align-center">
          <i class="fas fa-cloud-upload-alt me-2" />
          <span>{{ globalTranslate('folderManager.uploadingLabel') }}</span>
        </v-card-title>

        <v-card-text class="pa-6 mt-4">
          <div class="text-center">
            <!-- Uploading State -->
            <div v-if="uploadStatus === 'uploading'">
              <div class="spinner-wrapper mb-4">
                <div
                  class="spinner-border text-primary spinner-lg"
                  role="status"
                >
                  <span class="visually-hidden">{{ globalTranslate('loading') }}</span>
                </div>
              </div>
              <h5 class="mb-2 font-weight-bold">
                {{ globalTranslate('folderManager.uploadingLabel') }}
              </h5>
              <p class="text-muted mb-1">
                {{ course.id }}{{ languageToUpload?.code }}
              </p>
              <p class="mb-0 small text-secondary">
                {{ globalTranslate('folderManager.uploadingDesc') }}
              </p>
            </div>

            <!-- Success State -->
            <div v-if="uploadStatus === 'success'">
              <div class="status-icon-wrapper mb-4 animate-scale-in">
                <i class="fas fa-check-circle text-success status-icon-lg" />
              </div>
              <h5 class="text-success mb-2 font-weight-bold">
                {{ globalTranslate('folderManager.labels.uploadSuccess') }}
              </h5>
              <p class="text-muted mb-4">
                {{ course.id }}{{ languageToUpload?.code }}
              </p>
              <div class="alert alert-success d-inline-flex align-items-center py-2 px-3 mb-0">
                <i class="fas fa-info-circle me-2" />
                <span>{{ globalTranslate('folderManager.messages.uploadSuccess') }}</span>
              </div>
            </div>

            <!-- Error State -->
            <div v-if="uploadStatus === 'error'">
              <div class="status-icon-wrapper mb-4 animate-scale-in">
                <i class="fas fa-exclamation-circle text-danger status-icon-lg" />
              </div>
              <h5 class="text-danger mb-2 font-weight-bold">
                {{ globalTranslate('folderManager.labels.uploadError') }}
              </h5>
              <div class="alert alert-danger text-start py-3 px-3 mb-0">
                <div class="d-flex">
                  <i class="fas fa-times-circle mt-1 me-2" />
                  <span>{{ uploadErrorMessage }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <button
            type="button"
            class="btn btn-primary px-5 shadow-sm"
            :disabled="uploadStatus === 'uploading'"
            @click="closeUploadModal"
          >
            {{ globalTranslate('folderManager.buttons.close') }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Form -->
    <div>
      <div class="row mb-3">
        <div class="col-12">
          <div class="alert alert-info">
            <i class="fas fa-info-circle" />
            <strong>{{ globalTranslate('folderManager.title') }}</strong>
            {{ ` - ${globalTranslate('folderManager.infoAlert')}` }}
          </div>
        </div>
      </div>

      <div
        v-for="language in course.languages"
        :key="language.id"
        class="scorm-language-row"
      >
        <div class="row">
          <div class="col-md-2">
            <div class="form-label-group">
              <label class="form-label">
                <strong>{{ globalTranslate('folderManager.labels.language') }}</strong>
              </label>
              <div class="language-badge">
                <i class="fas fa-globe" /> {{ language.name }}
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-label-group">
              <label class="form-label">
                <strong>{{ globalTranslate('folderManager.labels.s3Folder') }}</strong>
              </label>
              <div class="folder-name">
                <i class="fas fa-folder" /> {{ `${course.id}${language.code}` }}
              </div>
            </div>
          </div>

          <div class="col-md-5">
            <div class="form-label-group">
              <label class="form-label">
                <strong>{{ globalTranslate('folderManager.labels.scormPackage') }}</strong>
              </label>
              <div class="file-input-wrapper">
                <input
                  :id="`file-${language.id}`"
                  type="file"
                  accept=".zip"
                  class="form-control"
                  :disabled="uploadStatus === 'uploading'"
                  @change="setFile(language, $event)"
                >
                <div
                  v-if="files[language.id]"
                  class="file-info mt-2"
                >
                  <i class="fas fa-file-archive text-primary" />
                  <span class="file-name">{{ files[language.id].name }}</span>
                  <span class="file-size text-muted">
                    {{ `(${formatFileSize(files[language.id].size)})` }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-label-group">
              <label class="form-label">&nbsp;</label>
              <button
                type="button"
                class="btn btn-primary w-100 text-nowrap"
                :disabled="!files[language.id] || uploadStatus === 'uploading'"
                @click="upload(language)"
              >
                <i class="fas fa-cloud-upload-alt" />
                {{ globalTranslate('folderManager.buttons.upload') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="isSnackbarOpen"
      :timeout="5000"
      location="top right"
    >
      <v-alert
        :color="alertColor"
        :title="title"
        :text="message"
      />
    </v-snackbar>

    <!-- Confirmation Modal -->
    <v-dialog
      v-model="isConfirmModalOpen"
      max-width="550px"
      persistent
    >
      <v-card class="confirm-modal-card">
        <v-card-title class="confirm-modal-header d-flex justify-space-between align-center">
          <div class="d-flex align-items-center">
            <i class="fas fa-cloud-upload-alt me-2" />
            <span>{{ globalTranslate('folderManager.buttons.confirmUpload') }}</span>
          </div>
          <v-btn
            variant="text"
            icon
            color="white"
            @click="isConfirmModalOpen = false"
          >
            <i class="fas fa-times" />
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6 px-6">
          <div
            v-if="languageToConfirm"
            class="confirm-modal-body text-center"
          >
            <div class="target-folder-display mb-4">
              <div class="label text-muted small text-uppercase mb-1">
                {{ globalTranslate('folderManager.labels.targetFolder') }}
              </div>
              <div class="folder-path">
                <i class="fas fa-folder-open me-2" />
                {{ `${course.id}/${languageToConfirm.code}` }}
              </div>
            </div>

            <p class="mb-4 lead-text">
              {{ globalTranslate('folderManager.messages.confirmUploadFor') }}
              <strong>{{ `${languageToConfirm.name}?` }}</strong>
            </p>

            <div class="alert alert-danger shadow-sm border-0 d-flex align-items-start text-start">
              <i class="fas fa-exclamation-triangle mt-1 me-3 text-danger h4 mb-0 pl-2" />
              <div>
                <strong>{{ globalTranslate('folderManager.labels.warning') }}</strong>
                {{ ` ${globalTranslate('folderManager.messages.folderReplacedWarning')}` }}
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <button
            type="button"
            class="btn btn-link text-decoration-none text-muted me-3"
            @click="isConfirmModalOpen = false"
          >
            {{ globalTranslate('folderManager.buttons.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary px-4 shadow-sm"
            @click="executeUpload"
          >
            {{ globalTranslate('folderManager.buttons.confirmUpload') }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';

import { lookup } from '../lookup/lookup';

export default {
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      globalTranslate,
      course: { ...this.modelValue },
      lookup,
      languageToUpload: null,
      isSnackbarOpen: false,
      message: '',
      title: '',
      alertColor: 'var(--scorm-alert-bg)',
      files: {},
      isConfirmModalOpen: false,
      languageToConfirm: null,
      isUploadModalOpen: false,
      uploadStatus: 'idle', // 'idle', 'uploading', 'success', 'error'
      uploadErrorMessage: '',
    };
  },

  methods: {
    setFile(language, event) {
      const [file] = event.target.files;
      this.files[language.id] = file;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';

      const BYTES_PER_UNIT = 1000; // SI unit
      const units = ['Bytes', 'KB', 'MB', 'GB'];
      const unitIndex = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT));
      const size = Math.round((bytes / (BYTES_PER_UNIT ** unitIndex)) * 100) / 100;

      return `${size} ${units[unitIndex]}`;
    },

    upload(language) {
      this.languageToConfirm = language;
      this.isConfirmModalOpen = true;
    },

    async executeUpload() {
      const language = this.languageToConfirm;
      this.isConfirmModalOpen = false;
      this.languageToConfirm = null;

      this.languageToUpload = language;
      this.uploadStatus = 'uploading';
      this.isUploadModalOpen = true;
      this.uploadErrorMessage = '';

      const formData = new FormData();
      if (this.files[language.id]) {
        formData.append('scorm_file', this.files[language.id]);
      }

      try {
        const response = await axios.post(`/s3upload/${this.course.id}/${language.code}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        this.uploadStatus = 'success';
        const message = response.data.message || `${globalTranslate('folderManager.messages.uploadSuccess')} (${this.course.id}${language.code})`;
        this.openSnackbar(globalTranslate('folderManager.labels.uploadSuccess'), message, 'success');

        // Clear the file input
        this.files[language.id] = null;
        const fileInput = document.getElementById(`file-${language.id}`);
        if (fileInput instanceof HTMLInputElement) {
          fileInput.value = '';
        }
      } catch (error) {
        this.uploadStatus = 'error';
        let errorMsg = `${globalTranslate('folderManager.messages.uploadFailed')} `;
        if (error.response && error.response.data) {
          if (error.response.data.message) {
            errorMsg += error.response.data.message;
          } else if (error.response.data.errors) {
            // Handle validation errors
            const errors = Object.values(error.response.data.errors).flat();
            errorMsg += errors.join(', ');
          }
        } else {
          errorMsg += globalTranslate('folderManager.messages.connectionError');
        }

        this.uploadErrorMessage = errorMsg;
        const statusCode = error.response ? error.response.status : 'Unknown';
        this.openSnackbar(`${globalTranslate('folderManager.labels.uploadError')} (${statusCode})`, errorMsg, 'error');
      }
    },

    closeUploadModal() {
      this.isUploadModalOpen = false;
      this.languageToUpload = null;
      this.uploadStatus = 'idle';
      this.uploadErrorMessage = '';
    },

    openSnackbar(title, message, type = 'info') {
      this.isSnackbarOpen = true;
      this.title = title;
      this.message = message;

      switch (type) {
        case 'success':
          this.alertColor = 'var(--scorm-success)';
          break;
        case 'error':
          this.alertColor = 'var(--scorm-error)';
          break;
        case 'warning':
          this.alertColor = 'var(--scorm-warning)';
          break;
        default:
          this.alertColor = 'var(--scorm-alert-bg)';
      }
    },
  },
};
</script>

<style scoped>
.scorm-upload-manager {
  padding: 1rem;
}

.upload-modal-card {
  border-radius: 12px;
  overflow: hidden;
}

.upload-modal-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 1.25rem 1.5rem;
  font-weight: 600;
  font-size: 1.25rem;
}

.animate-scale-in {
  animation: scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes scaleIn {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.spinner-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-lg {
  width: 4rem;
  height: 4rem;
  border-width: 0.3em;
}

.status-icon-lg {
  font-size: 5rem;
}

.scorm-language-row {
  background: var(--scorm-row-bg);
  border: 1px solid var(--scorm-row-border);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  transition: box-shadow 0.2s;
}

.scorm-language-row:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-label-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.language-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--scorm-badge-blue-bg);
  color: var(--scorm-badge-blue-text);
  border-radius: 4px;
  font-weight: 500;
}

.folder-name {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--scorm-badge-orange-bg);
  color: var(--scorm-badge-orange-text);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

.file-input-wrapper {
  position: relative;
  min-height: 90px;
}

.file-input-wrapper input[type="file"] {
  height: 40px;
  padding: 4px;
  border-radius: 4px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--scorm-badge-green-bg);
  border-radius: 4px;
  font-size: 0.875rem;
}

.file-name {
  font-weight: 500;
  color: var(--scorm-badge-green-text);
}

.file-size {
  font-size: 0.8rem;
}

.alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Confirmation Modal Styles */
.confirm-modal-card {
  border-radius: 12px;
  overflow: hidden;
}

.confirm-modal-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 1.25rem 1.5rem;
  font-weight: 600;
  font-size: 1.25rem;
}

.lead-text {
  font-size: 1.1rem;
  color: #37474f;
}

.target-folder-display {
  background: var(--scorm-badge-green-bg);
  border-radius: 8px;
  padding: 1rem;
  border: 1px dashed var(--scorm-badge-green-border);
}

.folder-path {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: var(--scorm-badge-green-text-dark);
  font-size: 1.1rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}
</style>
